/**
 * Carnivore Supply — Headless Checkout Tracking
 * ==============================================
 * PURPOSE: Tracks ecommerce events during the headless checkout phase.
 *          This file is loaded directly in theme.liquid for ALL pages.
 *
 * EVENTS FIRED BY THIS FILE:
 *   add_to_cart  — intercepted via fetch API when item added to cart
 *   view_item    — fired on product pages (data injected by cs-product-datalayer.liquid)
 *
 * EVENTS FIRED BY email-checkout.liquid (NOT this file):
 *   begin_checkout    — when user lands on /pages/email-checkout with cart
 *   generate_lead     — when user submits their email on checkout page
 *
 * PHASE SWITCH — When moving to native Shopify checkout:
 *   1. Set window.CS_TRACKING_PHASE = 'native' in theme.liquid OR
 *      comment out {% render 'cs-headless-tracking' %} in theme.liquid
 *   2. Register cs-customer-events.js as a Shopify Web Pixel:
 *      Shopify Admin → Settings → Customer Events → Add custom pixel
 *   3. The web pixel handles add_to_cart, checkout_started, order_created natively
 *
 * META PIXEL:
 *   fbq() calls are gated — they only run if the Meta pixel has been
 *   initialized (Pixel ID 127175192546454 — set in cs-customer-events.js web pixel).
 *   During the headless phase, Meta pixel fires on the storefront pages only.
 *
 * DO NOT edit this file to change the checkout flow — only tracking lives here.
 */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // ─── Phase guard ─────────────────────────────────────────────────────────────
  // Set window.CS_TRACKING_PHASE = 'native' in theme.liquid once you switch to
  // native Shopify checkout. This disables the fetch intercept below so there's
  // no double-firing alongside the Shopify Customer Events pixel.
  var PHASE = window.CS_TRACKING_PHASE || 'headless';

  // ─── Utilities ───────────────────────────────────────────────────────────────

  function formatPrice(cents) {
    return parseFloat((cents / 100).toFixed(2));
  }

  function getCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function safeVariantTitle(title) {
    return (!title || title === 'Default Title') ? undefined : title;
  }

  function fbqSafe() {
    if (typeof window.fbq === 'function') {
      window.fbq.apply(window, arguments);
    }
  }

  function twqSafe() {
    if (typeof window.twq === 'function') {
      window.twq.apply(window, arguments);
    }
  }

  // ─── Add to Cart — fetch intercept ───────────────────────────────────────────
  // Intercepts /cart/add.js AJAX calls made by Dawn theme's product form.
  // Fires add_to_cart to GTM dataLayer and Meta Pixel (if initialized).

  if (PHASE === 'headless') {
    var _origFetch = window.fetch;

    window.fetch = function (resource, options) {
      var url = typeof resource === 'string'
        ? resource
        : (resource && typeof resource.url === 'string' ? resource.url : '');

      var isCartAdd = url.indexOf('/cart/add') !== -1;

      if (isCartAdd) {
        return _origFetch.apply(this, arguments).then(function (response) {
          // Clone before consuming — response can only be read once
          response.clone().json().then(function (data) {
            // Shopify returns the added cart line item
            var price = formatPrice(data.final_price || 0);
            var qty   = data.quantity || 1;
            var value = parseFloat((price * qty).toFixed(2));

            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency: getCurrency(),
                value: value,
                items: [{
                  item_id:      data.sku || String(data.variant_id || ''),
                  item_name:    data.product_title || data.title || '',
                  item_variant: safeVariantTitle(data.variant_title),
                  price:        price,
                  quantity:     qty
                }]
              }
            });

            // Meta Pixel — AddToCart
            fbqSafe('track', 'AddToCart', {
              content_ids:  [String(data.variant_id || '')],
              content_name: data.product_title || data.title || '',
              content_type: 'product',
              value:        value,
              currency:     getCurrency()
            });

            // X Pixel — AddToCart
            twqSafe('event', 'tw-rb0tc-addtocart', {
              value:         value,
              currency:      getCurrency(),
              contents:      [{ content_id: String(data.variant_id || ''), content_name: data.product_title || data.title || '', num_items: qty }]
            });
          }).catch(function () { /* non-JSON or empty response — ignore */ });

          return response;
        });
      }

      return _origFetch.apply(this, arguments);
    };
  }

  // ─── View Item ───────────────────────────────────────────────────────────────
  // cs-product-datalayer.liquid sets window.cs_view_item_data on product pages.
  // We pick it up here so the push happens after GTM has loaded.

  document.addEventListener('DOMContentLoaded', function () {
    if (window.cs_view_item_data) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: window.cs_view_item_data
      });
    }
  });

})();
